import { useCallback, useState } from "react";
import {
  getPublicHolidaysReq,
  createPublicHolidayReq,
  updatePublicHolidayReq,
  deletePublicHolidayReq,
} from "services/api";

const parseErrorMessage = (err, fallbackMessage) => {
  return (
    err?.response?.data?.message ||
    (err?.response?.data?.errors && err.response.data.errors[0]?.msg) ||
    fallbackMessage
  );
};

function usePublicHolidays(adminId) {
  const [holidays, setHolidays] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchHolidays = useCallback(async () => {
    if (!adminId) return;
    setIsLoading(true);
    try {
      const { data } = await getPublicHolidaysReq({ admin_id: adminId });
      setHolidays(Array.isArray(data) ? data : []);
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, "Failed to load holidays"));
    }
    setIsLoading(false);
  }, [adminId]);

  const createHoliday = async ({ name, date }) => {
    if (!adminId) {
      setErrorMessage("Admin not found.");
      return false;
    }
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      await createPublicHolidayReq({
        admin_id: adminId,
        body: { name, date },
      });
      setSuccessMessage("Public holiday created successfully.");
      await fetchHolidays();
      return true;
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, "Failed to create public holiday."));
      return false;
    }
  };

  const createHolidaysInRange = async ({ name, dates }) => {
    if (!adminId) {
      setErrorMessage("Admin not found.");
      return false;
    }

    if (!dates || !dates.length) {
      setErrorMessage("Selected range has no weekdays.");
      return false;
    }

    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      for (const date of dates) {
        await createPublicHolidayReq({
          admin_id: adminId,
          body: { name, date },
        });
      }
      setSuccessMessage("Public holidays created successfully.");
      await fetchHolidays();
      return true;
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, "Failed to create public holidays."));
      return false;
    }
  };

  const updateHoliday = async ({ id, name, date }) => {
    if (!adminId) {
      setErrorMessage("Admin not found.");
      return false;
    }
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      await updatePublicHolidayReq({
        admin_id: adminId,
        id,
        body: { name, date },
      });
      setSuccessMessage("Public holiday updated successfully.");
      await fetchHolidays();
      return true;
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, "Failed to update public holiday."));
      return false;
    }
  };

  const archiveHoliday = async ({ id }) => {
    if (!adminId) {
      setErrorMessage("Admin not found.");
      return false;
    }
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      await deletePublicHolidayReq({
        admin_id: adminId,
        id,
      });
      setSuccessMessage("Public holiday archived successfully.");
      await fetchHolidays();
      return true;
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, "Failed to archive public holiday."));
      return false;
    }
  };

  const clearError = () => setErrorMessage(null);
  const clearSuccess = () => setSuccessMessage(null);

  return {
    holidays,
    isLoading,
    errorMessage,
    successMessage,
    fetchHolidays,
    createHoliday,
    createHolidaysInRange,
    updateHoliday,
    archiveHoliday,
    clearError,
    clearSuccess,
    setErrorMessage,
  };
}

export default usePublicHolidays;

